/**
 * Mini cart component with B2B pricing support
 * Displays cart items, quantities, pricing, discounts, promolines, and free shipping progress
 */

import clsx from 'clsx'

import { useEffect, useState, useMemo } from 'preact/hooks'
import { formatMoney, globalState, toProperCase, extractTextContent } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import useURL from 'hooks/useUrl'

import { createPortal } from 'preact/compat'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { usePromolines } from '@/components/hooks/usePromolines'
import { Promoline } from 'libs/athos/components/Promoline'
import { useGwp } from '@/components/hooks/useGwp'
import { GwpCartBlock, GwpGiftItem } from '@/components/custom-elements/gwp-cart-block'

/**
 *
 * Line item actions row: quantity selector and remove.
 * Mirrors the cart page's `cart-line-item` bottom row (quantity select + remove link).
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig, isLoading }) => {
  const [error, setError] = useState(null)

  /** Updates the line item to a new quantity (0 removes it). */
  const updateQuantity = async (newQuantity) => {
    try {
      /** Block quantities above the available inventory */
      if (item.inventory_quantity != null && newQuantity > item.inventory_quantity) {
        setError('Maximum quantity reached')
        setTimeout(() => setError(null), 5000)
        return
      }
      setError(null)
      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Update line item quantity */
      await window.Cart.updateLineItemQuantity({ id: item.variant_id, quantity: newQuantity }, { silent: false })
      /** Poll Shopify for new Cart item and set cart config state */
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (err) {
      globalEmitter.emit(emitterProps.notice.global, { type: 'error', content: err.message })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  const onQuantityChange = (e) => updateQuantity(parseInt(e.target.value, 10))
  const onRemove = (e) => {
    e.preventDefault()
    updateQuantity(0)
  }

  /** Quantity options 1..max, capped sensibly and always including the current quantity. */
  const maxQuantity = Math.max(item.quantity, Math.min(item.inventory_quantity ?? 10, 10))
  const quantityOptions = Array.from({ length: maxQuantity }, (_, index) => index + 1)

  return (
    <>
      <div className="flex items-center justify-between gap-x-0.5 md:gap-x-1">
        <div className="grow flex items-center justify-start">
          <label className="relative flex items-center w-fit h-[44px] py-0.25 cursor-pointer link-fade">
            <span className="absolute left-0 top-1/2 -translate-y-1/2 paragraph-xs whitespace-nowrap">Qty: </span>
            <select
              aria-label="Quantity"
              className="paragraph-xs leading-none h-full pl-2 pr-1 py-0 border-0 bg-none bg-transparent appearance-none cursor-pointer focus:outline-none focus:ring-0 focus:ring-offset-0 z-2"
              value={String(item.quantity)}
              disabled={isLoading}
              onChange={onQuantityChange}
            >
              {quantityOptions.map((quantity) => (
                <option key={quantity} value={String(quantity)}>
                  {quantity}
                </option>
              ))}
            </select>
            <span className="absolute right-0 top-1/2 -translate-y-1/2 size-0.75 pointer-events-none children:size-full">
              <Icon id={'chevron-down'} />
            </span>
          </label>
        </div>

        <button
          type="button"
          onClick={onRemove}
          disabled={isLoading}
          className="leading-none pb-0.375 appearance-none text-xs border-b border-b-type-black-primary rounded-none border-b-solid link-fade"
          aria-label="Remove item"
        >
          <span className="text-xs text-type-black-primary"> Remove </span>
        </button>
      </div>
      <p className={clsx('hidden w-full paragraph-xs text-type-error transition-all', error && '!flex')}>{error}</p>
    </>
  )
}

/**
 *
 *
 * Mini Cart Snippet,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }

  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  // Shortfall in cents — POSITIVE until the goal is met. Named so it can't be
  // mistaken for a boolean; derive success explicitly below.
  const remainingToGoal = freeShippingMinimum - cartSubTotal
  const isSuccess = remainingToGoal <= 0
  const message = isSuccess ? <p className="paragraph-xs">You're receiving free shipping</p> : <p className="paragraph-xs">You're <span className="font-medium">{formatMoney(remainingToGoal)} away</span> from free shipping</p>
  // Math.floor, not round: the cart page computes this bar in Liquid with
  // `divided_by`, which truncates. Rounding here would desync the two surfaces
  // by a point. Mirrors MiniCartSpendAndSave.
  const progressBarPercentage = Math.floor((cartSubTotal / freeShippingMinimum) * 100)
  const progressBarPercentageWidth = Math.min(progressBarPercentage, 100)
  return (
    <div data-role="free_shipping_monitor" className={`flex flex-col gap-0.375 px-1 md:px-1.5 py-1.375 ${isSuccess ? 'bg-page-success' : 'bg-page-secondary'}`}>
      <div className="flex items-center justify-center gap-x-0.375">
        {message}
      </div>
      <div className="relative w-full h-0.5 bg-page-tertiary">
        <div
          className={`absolute inset-tl w-shipping-bar-width h-full transition-all rounded-[4px] ${isSuccess ? 'bg-type-success' : 'bg-brand-black'}`}
          style={{ '--shipping-bar-width': progressBarPercentageWidth + '%' }}
        ></div>
      </div>
    </div>
  )
}

/**
 * Spend & Save monitor. Mirrors the free shipping bar, driven by the
 * `spend_and_save` metaobject tiers (sorted ascending by min_threshold).
 * Below the next tier it shows that tier's message (with `[money]` replaced by
 * the amount still required); above the highest tier it shows the success_message.
 *
 * Mirrors snippets/cart/spend-and-save.liquid — change both together.
 */
function MiniCartSpendAndSave({ cartTotal, tiers = [] }) {
  const currentCountry = window.globals?.localization?.country
  const filtered = currentCountry
    ? tiers.filter(
      (t) =>
        !t.available_countries?.length ||
          t.available_countries.some((c) => c.country_code === currentCountry)
    )
    : tiers
  const sorted = [...filtered].sort((a, b) => a.min_threshold - b.min_threshold)

  if (!sorted.length) return null

  const total = cartTotal || 0
  const nextTier = sorted.find((t) => total < t.min_threshold)
  const isSuccess = !nextTier

  let messageHtml = ''
  let percentage = 100
  let textColour
  let bgColour
  let defaultTextClass = ''
  let defaultBgClass = 'bg-page-secondary'

  if (nextTier) {
    const remaining = nextTier.min_threshold - total
    const money = `<span class="font-bold">${formatMoney(remaining)}</span>`
    messageHtml = extractTextContent(nextTier.message).replace(/\[money\]/g, money)
    percentage = Math.min(100, Math.floor((total / nextTier.min_threshold) * 100))
    textColour = nextTier.text_colour
    bgColour = nextTier.background_colour
  } else {
    const currentTier = sorted[sorted.length - 1]
    messageHtml = extractTextContent(currentTier.success_message)
    percentage = 100
    textColour = currentTier.success_text_colour
    bgColour = currentTier.success_background_colour
    defaultTextClass = 'text-type-black-primary'
    defaultBgClass = 'bg-page-success'
  }

  if (!messageHtml) return null

  const textClass = textColour ? 'text-[--ss-text-colour]' : defaultTextClass
  const bgClass = bgColour ? 'bg-[--ss-bg-colour]' : defaultBgClass

  return (
    <div
      data-role="spend_and_save_monitor"
      className={`flex flex-col justify-center items-center gap-0.5 h-auto px-1 md:px-1.5 py-1 transition-colors ${textClass} ${bgClass}`}
      style={{ '--ss-text-colour': textColour || '', '--ss-bg-colour': bgColour || '' }}
    >
      <div className="flex items-center text-center gap-x-0.375">
        <p className={`paragraph-xs ${isSuccess ? 'text-type-success' : 'text-type-black-secondary'}`} dangerouslySetInnerHTML={{ __html: messageHtml }} />
      </div>
      <div className="relative w-full h-0.5 rounded-[4px] bg-page-tertiary overflow-hidden">
        <div
          className={`absolute inset-tl w-shipping-bar-width h-full transition-all rounded-[4px] ${isSuccess ? 'bg-type-success' : 'bg-brand-black'}`}
          style={{ '--shipping-bar-width': percentage + '%' }}
        ></div>
      </div>
    </div>
  )
}

/**
 * B2B Line Price Component
 * Renders a b2b-price element directly in JSX
 */
const B2BLinePrice = ({ item }) => {
  return (
    <b2b-price className="group/price flex flex-col items-end text-right" data-variant-id={item.variant_id} data-quantity={item.quantity}>
      <span data-b2b-price className="paragraph-xs group-data-[sale=true]/price:font-medium group-data-[sale=true]/price:text-brand-sale"></span>
      <span data-original-price className="hidden group-data-[sale=true]/price:block paragraph-xs line-through text-type-black-tertiary"></span>
    </b2b-price>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const SalePrice = ({ salePrice, originalPrice }) => {
    return (
      <div className="flex flex-col items-end text-right">
        <span className="paragraph-xs font-medium text-brand-sale">{formatMoney(salePrice)}</span>
        <span className="paragraph-xs line-through text-type-black-tertiary">{formatMoney(originalPrice)}</span>
      </div>
    )
  }

  // For B2B customers, calculate line total from unit price
  const isB2BCustomer = window.globals?.customer?.b2b

  if (isB2BCustomer && item.variant_id) {
    return <B2BLinePrice item={item} />
  }

  /**
   * The strikethrough is the highest reference price on the line, not whichever
   * discount happens to apply first. A markdown product with a cart promo on top
   * discounts twice (RRP -> markdown -> promo), so `original_line_price` is only
   * the markdown price and would understate the saving. See RR-215.
   */
  const wasLinePrice = Math.max(item.original_line_price, (item.compare_at_price || 0) * item.quantity)

  if (wasLinePrice > item.line_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={wasLinePrice} />
  }

  return (
    <div className="flex flex-col items-end text-right">
      <span className="paragraph-xs">{formatMoney(item.line_price)}</span>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts && item.discounts.length > 0 && (
        <ul className="list-none">
          {item.discounts.map((discount) => {
            return (
              <li className="flex gap-x-0.25 items-center paragraph-xs text-type-black-secondary list-none flex-wrap text-balance">
                <span className="size-[14px] children:size-full">
                  <Icon id={'tag'} />
                </span>
                <span className="text-xs">
                {discount.title}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} className="shrink-0 link-fade" aria-label="Item image">
      <PreactImage className="flex w-[100px] md:w-[120px] aspect-4/5 children:size-full children:object-cover" key={id} imageUrl={[imageSrc]} />
    </a>
  )
}

/**
 * Checks if item has universal sizing by looking for "Size" property
 * @param {Object} properties - Item properties object
 * @returns {boolean} - True if universal sizing exists
 */
const hasUniversalSizing = (properties) => {
  return properties && Object.prototype.hasOwnProperty.call(properties, 'Size')
}

/**
 * Checks whether an option is the colour option, tolerating either spelling
 * @param {string} optionName - The option name
 * @returns {boolean} - True if the option is the colour option
 */
const isColourOption = (optionName) => {
  const name = optionName?.toLowerCase()
  return name === 'colour' || name === 'color'
}

/**
 * Formats size value based on gender and size array
 * @param {string} optionValue - The option value string
 * @param {string} gender - Product gender (Womens/Mens)
 * @returns {string} - Formatted size string or original value
 */
const formatSizeValue = (optionValue, gender) => {
  const itemSizesArray = optionValue.split(',')

  if (itemSizesArray.length <= 1) {
    return optionValue
  }

  let ukSizeValue = ''
  let usSizeValue = ''

  if (gender === 'Womens') {
    ukSizeValue = itemSizesArray[4] ? itemSizesArray[4].replace('WOMENS', '') : ''
    usSizeValue = itemSizesArray[5] ? itemSizesArray[5].replace('WOMENS', '') : ''
  } else if (gender === 'Mens') {
    ukSizeValue = itemSizesArray[6] ? itemSizesArray[6].replace('MENS', '') : ''
    usSizeValue = itemSizesArray[7] ? itemSizesArray[7].replace('MENS', '') : ''
  }

  const euSizeValue = itemSizesArray[3] ? itemSizesArray[3].replace('EURO', 'EU') : ''

  return `${usSizeValue} / ${ukSizeValue} / ${euSizeValue}`.trim()
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const properties = item.properties
  const hasProperties = properties && Object.keys(properties).length > 0
  const productGender = item.product?.metafields?.utopia?.gender

  // Filter options to exclude size when universal sizing exists
  const filteredOptions = item.options_with_values.filter(option => {
    if (option.name === 'Size' && hasUniversalSizing(properties)) {
      return false
    }
    return true
  })

  const criteria = useMemo(() => ({
    tags: item.tags ?? [],
    collections: item.collections?.map(c => c.handle) ?? [],
    products: [item.handle],
    location: 'Minicart'
  }), [item.tags, item.collections, item.handle])

  const allPromolines = usePromolines(criteria)

  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  return (
    <div className="flex flex-row justify-between gap-x-0.5 md:gap-x-1">
      <div className="flex flex-col">
        <h3 className="paragraph-xs font-medium text-type-black-primary">
          {item.customer_facing_product_name || item.product_title}
        </h3>
        <div className="flex flex-col gap-y-0.375 mt-0.375">
          {(item.colour_name || filteredOptions.length > 0 || hasProperties) && (
            <div className="flex flex-col">
              {[
                item.colour_name && `Colour: ${toProperCase(item.colour_name)}`,
                ...filteredOptions.map(option => {
                  if (option.name === 'Size') {
                    return `${option.name}: ${formatSizeValue(option.value, productGender)}`
                  }
                  /** Colour values come through uppercase from the PIM (e.g. `IVORY/MULTI`) */
                  if (isColourOption(option.name)) {
                    return `${option.name}: ${toProperCase(option.value)}`
                  }
                  return `${option.name}: ${option.value}`
                }),
                ...(hasProperties && properties ? Object.keys(properties)
                  .filter(property => properties[property] && !property.startsWith('_'))
                  .map(property => `${property}: ${properties[property]}`) : [])
              ].filter(Boolean).map((detail) => (
                <span key={detail} className="paragraph-xs text-type-black-secondary">{detail}</span>
              ))}
            </div>
          )}
             {(firstPromoline || secondPromoline) && (
        <div className="flex flex-col gap-0.25">
          {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="Minicart" promolineSize="small" />}
          {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="Minicart" promolineSize="small" />}
        </div>
             )}
          <MiniCartItemDiscount item={item} />
        </div>
      </div>
      <ItemPrice item={item} />
    </div>
  )
}

const MiniCartLineItem = ({ item, gift, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading && 'opacity-50'

  return (
    <div className={`flex flex-col gap-y-0.75 md:gap-y-1 pb-1 border-b border-borders-default transition-all last:pb-0 last:border-b-0 ${loadingState}`}>
      <div className="flex flex-row gap-x-0.75 md:gap-x-1">
        <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />
        <div className="flex-grow flex flex-col justify-between gap-y-1 size-full">
          <MiniCartItemDetails item={item} />
          <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
        </div>
      </div>
      {gift && (
        <GwpGiftItem
          gift={gift}
          onAddToCart={({ loading, cart: newCart }) => {
            if (loading !== undefined) handleSetCartConfig(CartConfigKeys.isLoading, loading)
            if (newCart) handleSetCartConfig(CartConfigKeys.cart, newCart)
          }}
          isLoading={isLoading}
          blockSpacing={true}
        />
      )}
    </div>
  )
}

/**
 * Calculates total savings from compare_at_price markdowns.
 *
 * Measured against `original_line_price` (the pre-discount price), NOT the final
 * line price, because the caller adds `cart.total_discount` on top. Measuring
 * against the final price would count cart promos in both figures. See RR-215.
 *
 * @param {Array} cartItems - Array of cart items
 * @returns {number} - Total compare_at_price savings in cents
 */
const calculateCompareAtSavings = (cartItems) => {
  let compareAtSavings = 0

  for (const item of cartItems) {
    const compareAtLinePrice = (item.compare_at_price || 0) * item.quantity

    if (compareAtLinePrice > item.original_line_price) {
      compareAtSavings += compareAtLinePrice - item.original_line_price
    }
  }

  return compareAtSavings
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, isLoading, cart, discountText, totalText }) => {
  const cartTotal = formatMoney(total)
  const compareAtSavings = calculateCompareAtSavings(cart.items)
  const totalDiscountAmount = totalCartDiscount + compareAtSavings
  const totalDiscount = formatMoney(totalDiscountAmount)
  const subtotal = formatMoney(total + totalDiscountAmount)
  const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <div className="flex flex-col gap-y-1 py-0.75 px-margin">
      <div className={`flex flex-col gap-y-0.5 transition-all ${isLoading && 'opacity-50'}`}>
        <div className="hidden gap-x-1">
          <p className="paragraph-s">{totalItems} {totalItems === 1 ? 'Item' : 'Items'}</p>
          <p className="paragraph-s">{subtotal}</p>
        </div>
        {globalState.customer.id === '' && (
        <div className="flex justify-between gap-x-1">
          <p className="paragraph-s text-type-black-secondary">Earn Rewards</p>
          <div className="flex flex-row gap-x-0.5">
          <p className="paragraph-s text-type-black-secondary flex items-center gap-x-0.25">
            <a href={globalState.routes.account_login_url} className="underline">Login</a>
            or
            <a href={globalState.routes.account_register_url} className="underline">Join now</a>
            </p>
          </div>
        </div>
        )}
        <div className="flex justify-between gap-x-1">
          <p className="paragraph-s text-brand-sale">{discountText || 'Savings'}</p>
          <p className="paragraph-s text-brand-sale">-{totalDiscount}</p>
        </div>
        <div className="flex justify-between gap-x-1">
        <p className="paragraph-s font-medium">{totalText || 'Total'}</p>
        <p className={`paragraph-s font-medium transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
      </div>
      </div>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading, emptyDescription, emptyLink, emptyLinkText }) => {
  return (
    <>
      <div className={`grow flex flex-col gap-y-1.5 items-start justify-start px-margin py-margin text-center transition-all ${isLoading ? 'opacity-0' : ''}`}>
        <p className={'text-type-black-primary paragraph-xs text-left'}>{emptyDescription}</p>
        {emptyLinkText &&
          <a href={emptyLink} className={'btn btn-black btn-outline black-outline btn-m w-full'} aria-label={emptyLinkText}><span>{emptyLinkText}</span></a>
        }
      </div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div className="px-margin py-0.75 border-t border-borders-default">
      <a href="/checkout" className="btn btn-primary btn-fill btn-m w-full" aria-label={checkoutButtonTitle}>
        <span>{checkoutButtonTitle}</span>
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, giftsWithoutThreshold, handleSetCartConfig, isLoading }) => {
  const giftsDisplayed = []
  return (
    <div className="grow flex flex-col gap-y-1 px-1 md:px-1.5 py-1.5" data-role="cart_items">
      {cart.items.map((item) => {
        const giftToDisplay = giftsWithoutThreshold.find(gift => {
          if (!gift.cartQualifies || gift.giftInCart || giftsDisplayed.includes(gift.id)) {
            return false
          }
          switch (gift.conditionalType) {
            case 'all':
              return true
            case 'products':
              return gift.conditionalIds.some(id => id.split('/').pop() === item.product_id.toString())
            case 'collections':
              return item.collections?.some(collection => gift.conditionalIds.some(id => id.split('/').pop() === collection.id.toString()))
            case 'tags':
              return item.tags?.some(tag => gift.conditionalTags.includes(tag))
            default:
              return false
          }
        })
        if (giftToDisplay) {
          giftsDisplayed.push(giftToDisplay.id)
        }
        return <MiniCartLineItem item={{ ...item }} gift={giftToDisplay} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart({ children }) {
  const path = useURL()
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum
  const { giftsWithoutThreshold } = useGwp(cart, freeShippingMinimum)

  const { checkoutButtonTitle, emptyDescription, emptyLink, emptyLinkText, discountText, totalText } = globalCart

  useEffect(() => {
    const isEmpty = cart.items.length <= 0
    const altPaymentButtons = document.querySelector('div.additional-checkout-buttons--vertical')
    if (altPaymentButtons) {
      altPaymentButtons.classList.toggle('hidden', isEmpty)
    }
    /** The summary wrapper is a static Liquid element (border, totals mount, express
     *  checkout). Hide it entirely when the cart is empty so it doesn't show a bare bar. */
    const summary = document.getElementById('minicart-summary')
    if (summary) {
      summary.classList.toggle('hidden', isEmpty)
    }
  }, [cartConfig])

  useEffect(() => {
    if (path.includes('timestamp=')) {
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }
  }, [path])

  return (
    <>
      {children}
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            {(target) => {
              /** Read the block settings off the mount point itself — see MiniCartPortal
               *  for why this can only be done from inside the portal. */
              const showShippingBar = target.dataset.showShippingBar !== 'false'
              const showSpendAndSave = target.dataset.showSpendAndSave !== 'false'
              if (!showShippingBar && !showSpendAndSave) return null
              return (
                <div className="flex items-stretch flex-col lg:flex-row w-full border-b border-borders-default children:grow lg:children:w-0 lg:children:border-r children:border-borders-default lg-max:children-last:border-b-0 lg:children-last:border-r-0">
                  {showShippingBar && (
                    <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />
                  )}
                  {showSpendAndSave && (
                    <MiniCartSpendAndSave cartTotal={cart.total_price} tiers={globalState.metaobjects?.spend_and_save} />
                  )}
                </div>
              )
            }}
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-gwp">
            <GwpCartBlock
              cart={cart}
              freeShippingMinimum={freeShippingMinimum}
              enableProgressBar={globalCart.gwp?.enableProgressBar}
              handleSetCartConfig={handleSetCartConfig}
              isLoading={isLoading}
              blockSpacing={true}
            />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} giftsWithoutThreshold={giftsWithoutThreshold} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary-mount">
            <div className="flex flex-col">
              <DisplayCartTotal
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                discountText={discountText}
                totalText={totalText}
              />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart cartConfig={cartConfig} globalCart={globalCart} isLoading={isLoading} emptyDescription={emptyDescription} emptyLink={emptyLink} emptyLinkText={emptyLinkText} />
        </MiniCartPortal>
      )}
    </>
  )
}

/**
 * Portals children into one of the static Liquid mount points inside <mini-cart>.
 *
 * `children` may be a function, which receives the resolved target element. That
 * is the only safe place to read Liquid block settings off a mount point:
 * preact-custom-element empties <mini-cart> before the first render, so the mount
 * points do not exist while MiniCart's own body runs — they are only in the DOM
 * by the time this component renders.
 */
const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  if (!target) return null
  return createPortal(typeof children === 'function' ? children(target) : children, target)
}
